import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { AuthService } from '../../services/auth.service';
import { PermissionsService } from '../../services/permissions.service';
import { ReputationService, ReputationScore, ReputationEvent } from '../../services/reputation.service';
import { PwaInstallSectionComponent } from '../pwa-install-section/pwa-install-section.component';
import { AvatarPickerComponent, AvatarConfig } from '../avatar-picker/avatar-picker.component';

interface UserProfile {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  dateOfBirth?: string;
  profileImage?: string;
  joinedDate: string;
  address?: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
}

interface UserScore {
  totalPoints: number;
  level: number;
  levelName: string;
  nextLevelPoints: number;
  pointsToNextLevel: number;
  badges: Badge[];
}

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  earnedDate: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
}

interface VerificationStatus {
  emailVerified: boolean;
  identityVerified: boolean;
  addressVerified: boolean;
  verificationLevel: 'basic' | 'intermediate' | 'advanced' | 'premium';
  trustScore: number;
}

interface PersonalStats {
  ordersCount: number;
  placesVisited: number;
  reviewsWritten: number;
  pointsEarned: number;
}

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, PwaInstallSectionComponent, AvatarPickerComponent],
  templateUrl: './user-profile.component.html',
  styleUrl: './user-profile.component.scss'
})
export class UserProfileComponent implements OnInit {
  currentUser: any = null;
  editMode = false;
  avatarConfig: AvatarConfig = { avatarId: 'face1', bgColor: '#22C55E' };
  profileForm: FormGroup;
  loading = false;
  saving = false;
  profileLoaded = false;
  activeSection = 'datos';
  
  userProfile: UserProfile = {
    id: 0,
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    joinedDate: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    }
  };

  userScore: UserScore = {
    totalPoints: 0,
    level: 0,
    levelName: '',
    nextLevelPoints: 0,
    pointsToNextLevel: 0,
    badges: []
  };

  verificationStatus: VerificationStatus = {
    emailVerified: false,
    identityVerified: false,
    addressVerified: false,
    verificationLevel: 'basic',
    trustScore: 0
  };

  personalStats: PersonalStats = {
    ordersCount: 0,
    placesVisited: 0,
    reviewsWritten: 0,
    pointsEarned: 0
  };

  achievements = [
    { icon: '🍕', label: 'Pedidos realizados', value: 0, color: 'primary' },
    { icon: '🏪', label: 'Restaurantes visitados', value: 0, color: 'success' },
    { icon: '⭐', label: 'Reseñas escritas', value: 0, color: 'warning' },
    { icon: '🎯', label: 'Nivel actual', value: this.userScore.level, color: 'info' }
  ];

  // Reputation
  reputationScore: ReputationScore = { score: 80, level: 'good', color: '#6B7280', events_count: 0 };
  reputationHistory: ReputationEvent[] = [];
  reputationLoading = false;

  // Notification settings state
  notifPermission: 'granted' | 'denied' | 'default' | 'unavailable' = 'default';
  notifTestSent = false;
  notifTestMessage = '';
  showIosInstallModal = false;
  isIos = false;
  isStandalone = false;
  hasSW = false;

  constructor(
    private authService: AuthService,
    private permissionsService: PermissionsService,
    private reputationService: ReputationService,
    private http: HttpClient,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.profileForm = this.fb.group({
      firstName: [this.userProfile.firstName, [Validators.required]],
      lastName: [this.userProfile.lastName, [Validators.required]],
      phone: [this.userProfile.phone, [Validators.required]],
      dateOfBirth: [this.userProfile.dateOfBirth, [Validators.required]],
      street: [this.userProfile.address?.street, [Validators.required]],
      city: [this.userProfile.address?.city, [Validators.required]],
      postalCode: [this.userProfile.address?.postalCode],
      country: [this.userProfile.address?.country]
    });
  }

  ngOnInit(): void {
    this.currentUser = this.authService.getCurrentUser();
    this.loadAvatarConfig();
    this.loadUserProfile();
    this.detectPlatform();
    this.refreshNotifStatus();
    this.route.queryParams.subscribe(params => {
      this.activeSection = params['section'] || 'datos';
      if (this.activeSection === 'nivel') {
        this.loadReputation();
      }
    });
  }

  toggleEditMode(): void {
    this.editMode = !this.editMode;
    if (!this.editMode) {
      // Reset form when canceling edit
      this.profileForm.patchValue({
        firstName: this.userProfile.firstName,
        lastName: this.userProfile.lastName,
        phone: this.userProfile.phone,
        dateOfBirth: this.userProfile.dateOfBirth,
        street: this.userProfile.address?.street,
        city: this.userProfile.address?.city,
        postalCode: this.userProfile.address?.postalCode,
        country: this.userProfile.address?.country
      });
    }
  }

  onSaveProfile(): void {
    if (this.profileForm.valid) {
      this.saving = true;
      const formValue = this.profileForm.value;
      
      const updateData = {
        first_name: formValue.firstName,
        last_name: formValue.lastName,
        phone: formValue.phone || null,
        date_of_birth: formValue.dateOfBirth || null,
        address: (formValue.street || formValue.city) ? {
          street: formValue.street || '',
          city: formValue.city || '',
          postal_code: formValue.postalCode || '',
          country: formValue.country || ''
        } : null
      };

      this.authService.updateUserProfile(updateData).subscribe({
        next: () => {
          // Actualizar los datos locales con los valores del formulario
          this.userProfile = {
            ...this.userProfile,
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            phone: formValue.phone,
            dateOfBirth: formValue.dateOfBirth,
            address: {
              street: formValue.street || '',
              city: formValue.city || '',
              postalCode: formValue.postalCode || '',
              country: formValue.country || ''
            }
          };

          this.editMode = false;
          this.saving = false;
        },
        error: (error: any) => {
          console.error('Error actualizando perfil:', error);
          this.saving = false;
        }
      });
    }
  }

  getBadgeRarityClass(rarity: string): string {
    switch (rarity) {
      case 'common': return 'badge-common';
      case 'rare': return 'badge-rare';
      case 'epic': return 'badge-epic';
      case 'legendary': return 'badge-legendary';
      default: return 'badge-common';
    }
  }

  getVerificationLevelClass(level: string): string {
    switch (level) {
      case 'basic': return 'verification-basic';
      case 'intermediate': return 'verification-intermediate';
      case 'advanced': return 'verification-advanced';
      case 'premium': return 'verification-premium';
      default: return 'verification-basic';
    }
  }

  getTrustScoreClass(score: number): string {
    if (score >= 90) return 'trust-excellent';
    if (score >= 70) return 'trust-good';
    if (score >= 50) return 'trust-fair';
    return 'trust-poor';
  }

  getLevelProgress(): number {
    const progress = (this.userScore.totalPoints - (this.userScore.nextLevelPoints - this.userScore.pointsToNextLevel)) / this.userScore.pointsToNextLevel * 100;
    return Math.min(100, Math.max(0, progress));
  }

  onVerifyEmail(): void {
    console.log('Verificar email');
    // Implementar lógica de verificación de email
  }

  onVerifyIdentity(): void {
    console.log('Verificar identidad');
    // Implementar lógica de verificación de identidad
  }

  onVerifyAddress(): void {
    console.log('Verificar dirección');
    // Implementar lógica de verificación de dirección
  }

  onChangePassword(): void {
    console.log('Cambiar contraseña');
    // Implementar navegación a cambio de contraseña
  }

  onDeleteAccount(): void {
    console.log('Eliminar cuenta');
    // Implementar confirmación y eliminación de cuenta
  }

  onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/']);
  }

  onAvatarChanged(config: AvatarConfig): void {
    this.avatarConfig = config;
    localStorage.setItem('barclik_avatar', JSON.stringify(config));
  }

  loadReputation(): void {
    this.reputationLoading = true;
    this.reputationService.getScore().subscribe({
      next: (score) => {
        this.reputationScore = score;
        this.reputationLoading = false;
      },
      error: () => {
        this.reputationLoading = false;
      }
    });
    this.reputationService.getHistory(5).subscribe({
      next: (events) => {
        this.reputationHistory = events;
      }
    });
  }

  getVerifiedCount(): number {
    let count = 0;
    if (this.verificationStatus.emailVerified) count++;
    if (this.verificationStatus.identityVerified) count++;
    if (this.verificationStatus.addressVerified) count++;
    return count;
  }

  isFieldMissing(field: string): boolean {
    if (!this.profileLoaded) return false;
    switch (field) {
      case 'phone': return !this.userProfile.phone;
      case 'dateOfBirth': return !this.userProfile.dateOfBirth;
      case 'address': return !this.userProfile.address?.street;
      default: return false;
    }
  }

  getReputationLevelLabel(): string {
    switch (this.reputationScore.level) {
      case 'excellent': return 'Excelente';
      case 'good': return 'Buena';
      case 'regular': return 'Regular';
      case 'low': return 'Baja';
      default: return 'Buena';
    }
  }

  private loadAvatarConfig(): void {
    try {
      const saved = localStorage.getItem('barclik_avatar');
      if (saved) {
        this.avatarConfig = JSON.parse(saved);
      }
    } catch {
      // Use defaults
    }
  }

  // --- Notification & platform methods ---

  private detectPlatform(): void {
    const ua = navigator.userAgent;
    this.isIos = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    this.hasSW = 'serviceWorker' in navigator && !!navigator.serviceWorker.controller;
  }

  refreshNotifStatus(): void {
    if (!('Notification' in window)) {
      this.notifPermission = 'unavailable';
      return;
    }
    this.notifPermission = Notification.permission as any;
  }

  async requestNotifPermission(): Promise<void> {
    // iOS without install => show modal
    if (this.isIos && !this.isStandalone) {
      this.showIosInstallModal = true;
      return;
    }
    const result = await this.permissionsService.requestNotificationPermission();
    this.refreshNotifStatus();
    if (result.granted) {
      this.notifTestMessage = 'Permiso concedido';
    }
  }

  sendTestNotification(): void {
    this.notifTestSent = false;
    this.notifTestMessage = '';
    const ok = this.permissionsService.showNotification('BarClik - Test', {
      body: 'Si ves esto, las notificaciones funcionan correctamente.',
      tag: 'test-notif',
    });
    this.notifTestSent = true;
    this.notifTestMessage = ok
      ? 'Notificacion enviada. Deberias verla ahora.'
      : 'No se pudo enviar. Revisa los permisos.';
  }

  closeIosModal(): void {
    this.showIosInstallModal = false;
  }

  private loadUserProfile(): void {
    this.loading = true;
    this.profileLoaded = false;
    console.log('loadUserProfile: iniciando carga...');

    this.http.get<any>('/api/v1/users/profile').subscribe({
      next: (profile) => {
        console.log('loadUserProfile: datos recibidos', profile);
        const nameParts = (profile.name || '').split(' ');
        const addr = profile.address;

        this.userProfile = {
          id: profile.id || 0,
          email: profile.email || '',
          firstName: nameParts[0] || '',
          lastName: nameParts.slice(1).join(' ') || '',
          phone: profile.phone || '',
          dateOfBirth: profile.date_of_birth || '',
          joinedDate: profile.created_at || '',
          address: {
            street: addr?.street || '',
            city: addr?.city || '',
            postalCode: addr?.postal_code || '',
            country: addr?.country || ''
          }
        };

        this.profileForm.patchValue({
          firstName: this.userProfile.firstName,
          lastName: this.userProfile.lastName,
          phone: this.userProfile.phone,
          dateOfBirth: this.userProfile.dateOfBirth,
          street: this.userProfile.address?.street,
          city: this.userProfile.address?.city,
          postalCode: this.userProfile.address?.postalCode,
          country: this.userProfile.address?.country
        });

        const emailV = !!profile.email_verified;
        this.verificationStatus = {
          emailVerified: emailV,
          identityVerified: false,
          addressVerified: false,
          verificationLevel: emailV ? 'intermediate' : 'basic',
          trustScore: Math.round((emailV ? 1 : 0) / 3 * 100)
        };

        this.achievements = [
          { icon: '🍕', label: 'Pedidos realizados', value: this.personalStats.ordersCount, color: 'primary' },
          { icon: '🏪', label: 'Restaurantes visitados', value: this.personalStats.placesVisited, color: 'success' },
          { icon: '⭐', label: 'Reseñas escritas', value: this.personalStats.reviewsWritten, color: 'warning' },
          { icon: '🎯', label: 'Nivel actual', value: this.userScore.level, color: 'info' }
        ];

        // Cache address for use in order flows
        if (this.userProfile.address?.street) {
          localStorage.setItem('barclik_profile_address', JSON.stringify(this.userProfile.address));
        }

        this.profileLoaded = true;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        console.error('loadUserProfile: ERROR', error);
        this.loading = false;
        this.profileLoaded = true;
        this.cdr.detectChanges();
        if (this.currentUser) {
          this.userProfile.email = this.currentUser.email || '';
          this.userProfile.firstName = this.currentUser.name?.split(' ')[0] || '';
          this.userProfile.lastName = this.currentUser.name?.split(' ').slice(1).join(' ') || '';
          this.profileForm.patchValue({
            firstName: this.userProfile.firstName,
            lastName: this.userProfile.lastName
          });
        }
      }
    });
  }
}
